import React, { useEffect, useRef, useState } from 'react';
import { marked } from 'marked';
import {
  generateReport,
  generateAiForGroup,
  getGroupMarkdown,
  getGroupPreviewHtml,
  uploadFinalPdf,
  deleteFinalPdf,
} from '../../Services/EditableReportService';
import { AdminProjectGroupRow } from '../../Types/EditableReport.types';

interface GroupDocumentsCardProps {
  projectId: string;
  projectName: string;
  group: AdminProjectGroupRow;
  /** Called after a mutation that changes report state (report created, PDF up/down)
   *  so the parent can re-fetch the project detail. */
  onChanged: () => void;
  onError: (message: string) => void;
}

/**
 * One card per project group showing its three documents (Resultados HTML,
 * Resultado IA, Informe final PDF) and the actions to generate/manage them.
 * Extracted from the legacy AdminReportEditor so it can live in the project
 * detail view. When the project has no report yet (group.reportId is null) the
 * only available action is "Generar informe", which creates the anchor report.
 */
const GroupDocumentsCard: React.FC<GroupDocumentsCardProps> = ({
  projectId,
  projectName,
  group,
  onChanged,
  onError,
}) => {
  const reportId = group.reportId;
  const groupId = group.projectGroupId;
  const isSubmitted = group.submitted === true;

  const [creatingReport, setCreatingReport] = useState(false);
  const [mdAvailable, setMdAvailable] = useState(group.markdownAvailable === true);
  const [mdGenerating, setMdGenerating] = useState(false);
  const [mdDownloading, setMdDownloading] = useState(false);
  const [mdPreviewing, setMdPreviewing] = useState(false);
  const [htmlPreviewing, setHtmlPreviewing] = useState(false);
  const [htmlDownloading, setHtmlDownloading] = useState(false);
  const [pdfUploading, setPdfUploading] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Keep local availability in sync if the parent refreshes the group.
  useEffect(() => {
    setMdAvailable(group.markdownAvailable === true);
  }, [group.markdownAvailable]);

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const hasPdf = !!group.finalPdfUrl;

  // --- Generate report (creates the anchor report for the whole project) ---
  const handleGenerateReport = async () => {
    setCreatingReport(true);
    try {
      await generateReport(projectId, groupId);
      onChanged();
    } catch (err: any) {
      onError(err.message || 'Error generando el informe');
      setCreatingReport(false);
    }
  };

  // --- AI markdown ---
  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const startPolling = (rid: string, previousGeneratedAt: string | null) => {
    stopPolling();
    setMdGenerating(true);
    pollingRef.current = setInterval(async () => {
      try {
        const { available, generatedAt } = await getGroupMarkdown(rid, groupId);
        if (available && generatedAt !== previousGeneratedAt) {
          stopPolling();
          setMdGenerating(false);
          setMdAvailable(true);
        }
      } catch {
        // keep trying
      }
    }, 3000);
  };

  const handleGenerateAi = async () => {
    if (!reportId) return;
    onError('');
    let previousGeneratedAt: string | null = null;
    if (mdAvailable) {
      try {
        const cur = await getGroupMarkdown(reportId, groupId);
        previousGeneratedAt = cur.generatedAt;
      } catch {
        // best-effort
      }
    }
    try {
      await generateAiForGroup(reportId, groupId);
      startPolling(reportId, previousGeneratedAt);
    } catch (err: any) {
      onError(err.message || 'Error generando el resultado IA del grupo');
      setMdGenerating(false);
    }
  };

  const handleDownloadMarkdown = async () => {
    if (!reportId) return;
    setMdDownloading(true);
    onError('');
    try {
      const { markdown: md } = await getGroupMarkdown(reportId, groupId);
      if (!md) {
        onError('Este grupo aún no tiene resultado IA generado.');
        return;
      }
      const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const safeProject = (projectName || 'proyecto').replace(/[\\/:*?"<>|]+/g, '-');
      const safeGroup = group.groupName.replace(/[\\/:*?"<>|]+/g, '-');
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeProject} - ${safeGroup} - valoracion-ia.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      onError(err.message || 'No se pudo descargar el markdown del grupo');
    } finally {
      setMdDownloading(false);
    }
  };

  const handlePreviewMarkdown = async () => {
    if (!reportId) return;
    const popup = window.open('about:blank', '_blank');
    if (!popup) {
      onError('El navegador bloqueó la apertura de la pestaña. Permite popups para esta página.');
      return;
    }
    popup.document.title = 'Cargando…';
    popup.document.body.innerHTML = '<p style="font-family:system-ui;padding:2rem;color:#666;">Cargando…</p>';
    setMdPreviewing(true);
    onError('');
    try {
      const { markdown: md } = await getGroupMarkdown(reportId, groupId);
      if (!md) {
        popup.close();
        onError('Este grupo aún no tiene resultado IA generado.');
        return;
      }
      const body = await marked.parse(md, { async: true });
      const escapeHtml = (s: string) =>
        s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      const docTitle = `${projectName} — ${group.groupName}`;
      const fullHtml = `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>${escapeHtml(docTitle)}</title>
<style>
  :root { color-scheme: light; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, system-ui, sans-serif; color: #1a1a1a; background: #fff; max-width: 820px; margin: 0 auto; padding: 3rem 2rem; line-height: 1.65; font-size: 16px; }
  h1, h2, h3, h4, h5 { font-weight: 600; line-height: 1.25; margin-top: 2.2rem; }
  h1 { font-size: 1.9rem; border-bottom: 1px solid #eee; padding-bottom: .5rem; }
  h2 { font-size: 1.45rem; } h3 { font-size: 1.2rem; }
  p { margin: 1rem 0; } ul, ol { padding-left: 1.4rem; } li { margin: .35rem 0; }
  blockquote { border-left: 4px solid #ddd; padding-left: 1rem; color: #555; margin: 1rem 0; }
  u { text-decoration-color: #f7aa28; text-decoration-thickness: 2px; }
  hr { border: none; border-top: 1px solid #eee; margin: 2rem 0; }
  .doc-meta { color: #999; font-size: .85rem; margin-bottom: 2rem; }
</style>
</head>
<body>
  <div class="doc-meta">${escapeHtml(projectName)} · ${escapeHtml(group.groupName)}</div>
  ${body}
</body>
</html>`;
      popup.document.open();
      popup.document.write(fullHtml);
      popup.document.close();
    } catch (err: any) {
      popup.close();
      onError(err.message || 'No se pudo abrir la previsualización');
    } finally {
      setMdPreviewing(false);
    }
  };

  // --- HTML (responses) ---
  const handlePreviewHtml = async () => {
    if (!reportId) return;
    const popup = window.open('about:blank', '_blank');
    if (!popup) {
      onError('El navegador bloqueó la apertura de la pestaña. Permite popups para esta página.');
      return;
    }
    popup.document.title = 'Cargando…';
    popup.document.body.innerHTML = '<p style="font-family:system-ui;padding:2rem;color:#666;">Cargando…</p>';
    setHtmlPreviewing(true);
    onError('');
    try {
      const html = await getGroupPreviewHtml(reportId, groupId);
      popup.document.open();
      popup.document.write(html);
      popup.document.close();
    } catch (err: any) {
      popup.close();
      onError(err.message || 'No se pudo abrir la previsualización');
    } finally {
      setHtmlPreviewing(false);
    }
  };

  const handleDownloadHtml = async () => {
    if (!reportId) return;
    setHtmlDownloading(true);
    onError('');
    try {
      const html = await getGroupPreviewHtml(reportId, groupId);
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const safeProject = (projectName || 'proyecto').replace(/[\\/:*?"<>|]+/g, '-');
      const safeGroup = group.groupName.replace(/[\\/:*?"<>|]+/g, '-');
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeProject} - ${safeGroup} - resultados.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      onError(err.message || 'No se pudo descargar el HTML del grupo');
    } finally {
      setHtmlDownloading(false);
    }
  };

  // --- Final PDF ---
  const validatePdfFile = (file: File): string | null => {
    if (file.type !== 'application/pdf') return 'El fichero debe ser un PDF.';
    if (file.size > 20 * 1024 * 1024) return 'El PDF no puede superar los 20 MB.';
    return null;
  };

  const handleUploadPdf = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !reportId) return;
    const validationError = validatePdfFile(file);
    if (validationError) {
      onError(validationError);
      return;
    }
    if (hasPdf && !window.confirm('¿Reemplazar el informe final actual?')) return;
    setPdfUploading(true);
    onError('');
    try {
      await uploadFinalPdf(reportId, groupId, file);
      onChanged();
    } catch (err: any) {
      onError(err.message || 'Error subiendo el informe final');
    } finally {
      setPdfUploading(false);
    }
  };

  const handleDeletePdf = async () => {
    if (!reportId) return;
    if (!window.confirm('¿Eliminar el informe final?')) return;
    setPdfUploading(true);
    onError('');
    try {
      await deleteFinalPdf(reportId, groupId);
      onChanged();
    } catch (err: any) {
      onError(err.message || 'Error eliminando el informe final');
    } finally {
      setPdfUploading(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center bg-light">
        <div>
          <i className="bi bi-folder me-2"></i>
          <strong>{group.groupName}</strong>
        </div>
        <div className="d-flex align-items-center gap-2">
          {/* Respuestas del cliente: distinto de "enviado" (acción del arquitecto). */}
          <span
            className={`badge ${group.responseCount > 0 ? 'bg-info-subtle text-info-emphasis' : 'bg-light text-muted border'}`}
            title={group.responseCount > 0
              ? 'Respuestas del cliente registradas en este grupo'
              : 'El cliente todavía no ha respondido nada en este grupo'}
          >
            <i className={`bi ${group.responseCount > 0 ? 'bi-chat-left-text' : 'bi-chat-left'} me-1`}></i>
            {group.responseCount === 0
              ? 'Sin respuestas'
              : `${group.responseCount} ${group.responseCount === 1 ? 'respuesta' : 'respuestas'}`}
          </span>
          {/* Envío del grupo: lo marca el arquitecto, no implica que el cliente haya respondido. */}
          <span
            className={`badge ${isSubmitted ? 'bg-success-subtle text-success-emphasis' : 'bg-secondary-subtle text-secondary-emphasis'}`}
            title={isSubmitted
              ? 'El arquitecto ha enviado/cerrado este grupo'
              : 'El arquitecto todavía no ha enviado este grupo'}
          >
            <i className={`bi ${isSubmitted ? 'bi-send-check' : 'bi-send'} me-1`}></i>
            {isSubmitted ? 'Enviado' : 'No enviado'}
          </span>
        </div>
      </div>

      {!reportId ? (
        // No anchor report yet for the project: only offer to generate it.
        <div className="card-body d-flex justify-content-between align-items-center">
          <span className="text-muted">
            {isSubmitted
              ? 'Aún no se ha generado el informe de este grupo.'
              : 'El grupo todavía no se ha enviado.'}
          </span>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={handleGenerateReport}
            disabled={creatingReport || !isSubmitted}
            title={!isSubmitted ? 'El grupo todavía no se ha enviado' : 'Generar el informe de este grupo'}
          >
            {creatingReport ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                Generando...
              </>
            ) : (
              <>
                <i className="bi bi-stars me-1"></i>
                Generar informe
              </>
            )}
          </button>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-borderless table-hover align-middle mb-0">
            <tbody>
              {/* HTML row */}
              <tr>
                <td>
                  <i className="bi bi-filetype-html me-2"></i>
                  Resultados del test (HTML)
                </td>
                <td className="text-end" style={{ width: '180px' }}>
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-dark"
                    onClick={handlePreviewHtml}
                    disabled={htmlPreviewing || !isSubmitted}
                    title={!isSubmitted ? 'El grupo todavía no se ha enviado' : 'Ver en una pestaña nueva'}
                  >
                    {htmlPreviewing ? <span className="spinner-border spinner-border-sm" role="status"></span> : <i className="bi bi-eye"></i>}
                  </button>
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-dark ms-1"
                    onClick={handleDownloadHtml}
                    disabled={htmlDownloading || !isSubmitted}
                    title={!isSubmitted ? 'El grupo todavía no se ha enviado' : 'Descargar HTML de este grupo'}
                  >
                    {htmlDownloading ? <span className="spinner-border spinner-border-sm" role="status"></span> : <i className="bi bi-download"></i>}
                  </button>
                </td>
              </tr>

              {/* AI markdown row */}
              <tr>
                <td>
                  <i className="bi bi-filetype-md me-2"></i>
                  Resultado IA (markdown)
                </td>
                <td className="text-end">
                  {mdAvailable ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-link btn-sm text-dark"
                        onClick={handlePreviewMarkdown}
                        disabled={mdPreviewing || mdGenerating}
                        title="Ver en una pestaña nueva"
                      >
                        {mdPreviewing ? <span className="spinner-border spinner-border-sm" role="status"></span> : <i className="bi bi-eye"></i>}
                      </button>
                      <button
                        type="button"
                        className="btn btn-link btn-sm text-dark ms-1"
                        onClick={handleDownloadMarkdown}
                        disabled={mdDownloading || mdGenerating}
                        title="Descargar markdown IA de este grupo"
                      >
                        {mdDownloading ? <span className="spinner-border spinner-border-sm" role="status"></span> : <i className="bi bi-download"></i>}
                      </button>
                      <button
                        type="button"
                        className="btn btn-link btn-sm text-muted ms-1"
                        onClick={handleGenerateAi}
                        disabled={mdGenerating || !isSubmitted}
                        title={!isSubmitted ? 'El grupo todavía no se ha enviado' : 'Regenerar resultado IA con los prompts actuales del grupo'}
                      >
                        {mdGenerating ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                            Regenerando...
                          </>
                        ) : (
                          <i className="bi bi-arrow-clockwise"></i>
                        )}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-muted"
                      onClick={handleGenerateAi}
                      disabled={mdGenerating || !isSubmitted}
                      title={!isSubmitted ? 'El grupo todavía no se ha enviado' : 'Generar resultado IA para este grupo'}
                    >
                      {mdGenerating ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                          Generando...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-stars me-1"></i>
                          Generar
                        </>
                      )}
                    </button>
                  )}
                </td>
              </tr>

              {/* Final PDF row */}
              <tr>
                <td>
                  <i className="bi bi-filetype-pdf me-2"></i>
                  <strong>Informe final (PDF)</strong>
                </td>
                <td className="text-end">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,application/pdf"
                    style={{ display: 'none' }}
                    onChange={handleUploadPdf}
                  />
                  {pdfUploading ? (
                    <span className="btn btn-link btn-sm text-muted disabled">
                      <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                      Procesando...
                    </span>
                  ) : hasPdf ? (
                    <>
                      <a
                        className="btn btn-link btn-sm text-dark"
                        href={group.finalPdfPreviewUrl || group.finalPdfUrl || undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Ver en una pestaña nueva"
                      >
                        <i className="bi bi-eye"></i>
                      </a>
                      <a
                        className="btn btn-link btn-sm text-dark"
                        href={group.finalPdfUrl || undefined}
                        download={`${projectName}-${group.groupName}-informe-final.pdf`}
                        title="Descargar"
                      >
                        <i className="bi bi-download"></i>
                      </a>
                      <button
                        type="button"
                        className="btn btn-link btn-sm text-dark"
                        onClick={() => fileInputRef.current?.click()}
                        title="Reemplazar"
                      >
                        <i className="bi bi-arrow-repeat"></i>
                      </button>
                      <button
                        type="button"
                        className="btn btn-link btn-sm text-danger"
                        onClick={handleDeletePdf}
                        title="Eliminar"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-muted"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <i className="bi bi-upload me-1"></i>
                      Subir
                    </button>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GroupDocumentsCard;
